import type { Action, Computed, Thunk } from 'easy-peasy';
import { action, computed, thunk } from 'easy-peasy';
import type { StoreModel } from '..';
import type {
  AddressCreateParams,
  AddressUpdateParams,
  FulfillmentContextDetail,
  ContactCreateParams,
  ContactUpdateParams,
  LabelProperties,
  LabelPropertiesCreateParams,
  LabelPropertiesUpdateParams,
  CarrierSettings,
  Contract,
  Augmentation,
  ScheduleCreateParams,
  ScheduleUpdateParams,
  Schedule,
  ThirdPartyBillingAccount,
  UPSRegistration,
  FedexRegistration,
  ScheduleEntry,
} from '../services/FP_FulfillmentContextService/types';
import parseStatusCode from '../errors/parseStatusCode';
import type { Injections } from '../services/injections';
import type { ApplicationErrorOptions } from '../../types';
import type {
  LinkOriginParams,
  AuditInfoWithAsset,
  ConfiguredCarrier,
  ConfiguredCarrierServiceMethod,
  CreateFulfillmentContextParams,
  CreateTenantParams,
  FulfillmentContextDetails,
  LinkAddressParams,
  LinkContactParams,
  LinkTagParams,
  LinkTenantParams,
  UpdateFulfillmentContextParams,
  UpdateTenantParams,
  CreateOriginParams,
  HydratedOrigin,
  LinkContractParams,
  HydratedContract,
  LinkLabelPropertyParams,
  CarrierSettingsCreateParams,
  CarrierSettingsUpdateParams,
  LinkCarrierSettingsParams,
  CarrierAccountUpdateParams,
  CarrierAccountCreateParams,
  LinkCarrierAccountParams,
  InjectionProfileCreateParams,
  InjectionProfile,
  InjectionProfileUpdateParams,
  LinkToInjectionProfileParams,
  InjectionSite,
  InjectionSiteCreateParams,
  InjectionSiteUpdateParams,
  LinkToInjectionSiteParams,
  CreateLabelAugmentationParams,
  LinkLabelAugmentationParams,
  UpdateLabelAugmentationParams,
  LinkScheduleParams,
  UpdateOriginParams,
  ContractUpdateParams,
  CreateThirdPartyBillingAccountParams,
  LinkThirdPartyBillingAccountParams,
  LinkThirdPartyBillingAccountSetParams,
  HydratedThirdPartyBillingAccountSet,
  UnlinkTenant,
  UpdateReturnToAddressOverrideParams,
  UnlinkReturnToAddressOverride,
  DockLanePostalCodeResponse,
  FetchDockLanePostalCodeMappingParams,
  LinkAddressToReturnToAddressOverride,
  LinkContactToReturnToAddressOverride,
} from './types';
import type { ContextTableSummary } from './helpers';
import {
  carrierServiceMethodSummarizer,
  carrierSummarizer,
  fulfillmentContextAudit,
  buildContextTableSummaries,
} from './helpers';
import { hydrateThirdPartyBillingAccountSet } from './assetHydration/hydrateThirdPartyBillingAccountSet';
import { hydrateContracts } from './assetHydration/hydrateContracts';
import { hydrateOrigins } from './assetHydration/hydrateOrigins';
import type {
  PartnerAddressV2,
  PartnerCarrierAccountV2,
  PartnerContact,
  PartnerLaneOrchestration,
  PartnerLaneSortConfigurationOrchestration,
  PartnerOrigin,
  PartnerPostalCodeMappingCreate,
  PartnerReturnToAddressOverrideCreate,
  PartnerThirdPartyBillingAccountSet,
} from '@shipium-corp/shipium-ts-types';
import type { ReturnToAddressOverride } from '../assets/types';

export interface FP_FulfillmentContextModel {
  /***
   * Loading
   */
  lastAction?: number;
  fulfillmentContextActionStart: Action<FP_FulfillmentContextModel, void>;
  /***
   * Fulfillment Context Summary
   */
  configuredCarriers: Computed<FP_FulfillmentContextModel, (contextId?: string) => ConfiguredCarrier[]>;
  configuredCarrierServiceMethods: Computed<
    FP_FulfillmentContextModel,
    (contextId?: string) => ConfiguredCarrierServiceMethod[]
  >;
  contextAuditInfoSummary: Computed<
    FP_FulfillmentContextModel,
    (contextId: string) => AuditInfoWithAsset[] | undefined
  >;
  /***
   * Fulfillment Context Details
   */
  fulfillmentContextDetails: FulfillmentContextDetails;

  fetchFulfillmentContextDetails: Thunk<
    FP_FulfillmentContextModel,
    void,
    Injections,
    StoreModel,
    Promise<FulfillmentContextDetail[]>
  >;
  fetchFulfillmentContextDetailByContextId: Thunk<
    FP_FulfillmentContextModel,
    string,
    Injections,
    StoreModel,
    Promise<FulfillmentContextDetail[]>
  >;
  setFulfillmentContextDetails: Action<FP_FulfillmentContextModel, FulfillmentContextDetail[]>;
  setFulfillmentContextDetail: Action<FP_FulfillmentContextModel, FulfillmentContextDetail>;
  fetchFulfillmentContextDetailsFailure: Action<FP_FulfillmentContextModel, ApplicationErrorOptions>;
  fetchFulfillmentContextDetailFailure: Action<FP_FulfillmentContextModel, ApplicationErrorOptions>;
  /***
   * Fulfillment Context Mutations
   */
  createFulfillmentContext: Thunk<
    FP_FulfillmentContextModel,
    CreateFulfillmentContextParams,
    Injections,
    StoreModel,
    Promise<string>
  >;
  mutateFulfillmentContextFailure: Action<FP_FulfillmentContextModel, ApplicationErrorOptions>;
  deleteFulfillmentContext: Thunk<FP_FulfillmentContextModel, string, Injections, StoreModel, Promise<void>>;
  updateFulfillmentContext: Thunk<
    FP_FulfillmentContextModel,
    UpdateFulfillmentContextParams,
    Injections,
    StoreModel,
    Promise<string>
  >;
  createReturnToAddressOverride: Thunk<
    FP_FulfillmentContextModel,
    PartnerReturnToAddressOverrideCreate & { fulfillmentContextId: string },
    Injections,
    StoreModel,
    Promise<string>
  >;
  updateReturnToAddressOverride: Thunk<
    FP_FulfillmentContextModel,
    UpdateReturnToAddressOverrideParams,
    Injections,
    StoreModel,
    Promise<ReturnToAddressOverride>
  >;
  unlinkReturnToAddressOverride: Thunk<
    FP_FulfillmentContextModel,
    UnlinkReturnToAddressOverride,
    Injections,
    StoreModel,
    Promise<string>
  >;
  linkReturnToAddressOverride: Thunk<
    FP_FulfillmentContextModel,
    UnlinkReturnToAddressOverride,
    Injections,
    StoreModel,
    Promise<string>
  >;
  linkAddressToReturnToAddressOverride: Thunk<
    FP_FulfillmentContextModel,
    LinkAddressToReturnToAddressOverride,
    Injections,
    StoreModel,
    Promise<string>
  >;
  unlinkAddressFromReturnToAddressOverride: Thunk<
    FP_FulfillmentContextModel,
    LinkAddressToReturnToAddressOverride,
    Injections,
    StoreModel,
    Promise<void>
  >;
  linkContactToReturnToAddressOverride: Thunk<
    FP_FulfillmentContextModel,
    LinkContactToReturnToAddressOverride,
    Injections,
    StoreModel,
    Promise<string>
  >;
  unlinkContactFromReturnToAddressOverride: Thunk<
    FP_FulfillmentContextModel,
    LinkContactToReturnToAddressOverride,
    Injections,
    StoreModel,
    Promise<void>
  >;
  createTenant: Thunk<FP_FulfillmentContextModel, CreateTenantParams, Injections, StoreModel, Promise<string>>;
  updateTenant: Thunk<FP_FulfillmentContextModel, UpdateTenantParams, Injections, StoreModel, Promise<string>>;
  linkTenant: Thunk<FP_FulfillmentContextModel, LinkTenantParams, Injections, StoreModel, Promise<string>>;
  unlinkTenant: Thunk<FP_FulfillmentContextModel, UnlinkTenant, Injections, StoreModel, Promise<string>>;
  createOrigin: Thunk<FP_FulfillmentContextModel, CreateOriginParams, Injections, StoreModel, Promise<string | null>>;
  linkOrigin: Thunk<FP_FulfillmentContextModel, LinkOriginParams, Injections, StoreModel, Promise<string>>;
  updateOrigin: Thunk<FP_FulfillmentContextModel, UpdateOriginParams, Injections, StoreModel, Promise<string | null>>;

  /***
   * Shared assets
   */
  addresses: Record<string, PartnerAddressV2>;
  fetchAddressAssets: Thunk<FP_FulfillmentContextModel, void, Injections, StoreModel, Promise<PartnerAddressV2[]>>;
  createAddress: Thunk<
    FP_FulfillmentContextModel,
    AddressCreateParams,
    Injections,
    StoreModel,
    Promise<PartnerAddressV2>
  >;
  updateAddress: Thunk<
    FP_FulfillmentContextModel,
    AddressUpdateParams,
    Injections,
    StoreModel,
    Promise<PartnerAddressV2>
  >;
  setAddresses: Action<FP_FulfillmentContextModel, PartnerAddressV2[]>;
  setAddress: Action<FP_FulfillmentContextModel, PartnerAddressV2>;
  linkAddress: Thunk<FP_FulfillmentContextModel, LinkAddressParams, Injections, StoreModel, Promise<void>>;

  contacts: Record<string, PartnerContact>;
  fetchContactAssets: Thunk<FP_FulfillmentContextModel, void, Injections, StoreModel, Promise<PartnerContact[]>>;
  createContact: Thunk<
    FP_FulfillmentContextModel,
    ContactCreateParams,
    Injections,
    StoreModel,
    Promise<PartnerContact>
  >;
  updateContact: Thunk<
    FP_FulfillmentContextModel,
    ContactUpdateParams,
    Injections,
    StoreModel,
    Promise<PartnerContact>
  >;
  setContacts: Action<FP_FulfillmentContextModel, PartnerContact[]>;
  setContact: Action<FP_FulfillmentContextModel, PartnerContact>;
  linkContact: Thunk<FP_FulfillmentContextModel, LinkContactParams, Injections, StoreModel, Promise<void>>;
  linkTag: Thunk<FP_FulfillmentContextModel, LinkTagParams, Injections, StoreModel, Promise<void>>;

  schedules: Record<string, Schedule>;
  fetchScheduleAssets: Thunk<FP_FulfillmentContextModel, void, Injections, StoreModel, Promise<Schedule[]>>;
  createSchedule: Thunk<
    FP_FulfillmentContextModel,
    ScheduleCreateParams,
    Injections,
    StoreModel,
    Promise<PartnerContact>
  >;
  updateSchedule: Thunk<
    FP_FulfillmentContextModel,
    ScheduleUpdateParams,
    Injections,
    StoreModel,
    Promise<PartnerContact>
  >;
  setSchedules: Action<FP_FulfillmentContextModel, Schedule[]>;
  setSchedule: Action<FP_FulfillmentContextModel, Schedule>;
  linkSchedule: Thunk<FP_FulfillmentContextModel, LinkScheduleParams, Injections, StoreModel, Promise<void>>;

  labelProperties: Record<string, LabelProperties>;
  fetchLabelProperties: Thunk<FP_FulfillmentContextModel, void, Injections, StoreModel, Promise<LabelProperties[]>>;
  createLabelProperties: Thunk<
    FP_FulfillmentContextModel,
    LabelPropertiesCreateParams,
    Injections,
    StoreModel,
    Promise<LabelProperties>
  >;
  updateLabelProperties: Thunk<
    FP_FulfillmentContextModel,
    LabelPropertiesUpdateParams,
    Injections,
    StoreModel,
    Promise<LabelProperties>
  >;
  setLabelProperties: Action<FP_FulfillmentContextModel, LabelProperties[]>;
  setLabelProperty: Action<FP_FulfillmentContextModel, LabelProperties>;
  linkLabelProperty: Thunk<FP_FulfillmentContextModel, LinkLabelPropertyParams, Injections, StoreModel, Promise<void>>;

  labelAugmentations: Record<string, Augmentation>;
  fetchLabelAugmentations: Thunk<FP_FulfillmentContextModel, void, Injections, StoreModel, Promise<Augmentation[]>>;
  createLabelAugmentation: Thunk<
    FP_FulfillmentContextModel,
    CreateLabelAugmentationParams,
    Injections,
    StoreModel,
    Promise<Augmentation>
  >;
  updateLabelAugmentation: Thunk<
    FP_FulfillmentContextModel,
    UpdateLabelAugmentationParams,
    Injections,
    StoreModel,
    Promise<Augmentation>
  >;
  setLabelAugmentations: Action<FP_FulfillmentContextModel, Augmentation[]>;
  setLabelAugmentation: Action<FP_FulfillmentContextModel, Augmentation>;
  linkLabelAugmentation: Thunk<
    FP_FulfillmentContextModel,
    LinkLabelAugmentationParams,
    Injections,
    StoreModel,
    Promise<void>
  >;

  origins: Record<string, PartnerOrigin>;
  hydratedOrigins: Record<string, HydratedOrigin>;
  fetchOrigins: Thunk<FP_FulfillmentContextModel, void, Injections, StoreModel, Promise<HydratedOrigin[]>>;
  setOrigins: Action<FP_FulfillmentContextModel, PartnerOrigin[]>;
  setHydratedOrigins: Action<FP_FulfillmentContextModel, HydratedOrigin[]>;

  contracts: Record<string, Contract>;
  hydratedContracts: Record<string, HydratedContract>;
  fetchContracts: Thunk<FP_FulfillmentContextModel, void, Injections, StoreModel, Promise<any[]>>;
  setHydratedContracts: Action<FP_FulfillmentContextModel, HydratedContract[]>;
  setContracts: Action<FP_FulfillmentContextModel, Contract[]>;
  carrierSettings: Record<string, CarrierSettings>;
  createCarrierSettings: Thunk<
    FP_FulfillmentContextModel,
    CarrierSettingsCreateParams,
    Injections,
    StoreModel,
    Promise<CarrierSettings>
  >;
  updateCarrierSettings: Thunk<
    FP_FulfillmentContextModel,
    CarrierSettingsUpdateParams,
    Injections,
    StoreModel,
    Promise<CarrierSettings>
  >;
  fetchCarrierSettings: Thunk<FP_FulfillmentContextModel, void, Injections, StoreModel, Promise<CarrierSettings[]>>;
  setCarrierSettings: Action<FP_FulfillmentContextModel, CarrierSettings[]>;
  linkCarrierSettings: Thunk<
    FP_FulfillmentContextModel,
    LinkCarrierSettingsParams,
    Injections,
    StoreModel,
    Promise<void>
  >;
  setContract: Action<FP_FulfillmentContextModel, Contract>;
  updateCarrierContract: Thunk<
    FP_FulfillmentContextModel,
    ContractUpdateParams,
    Injections,
    StoreModel,
    Promise<Contract>
  >;
  linkCarrierContract: Thunk<FP_FulfillmentContextModel, LinkContractParams, Injections, StoreModel, Promise<void>>;

  createCarrierAccount: Thunk<
    FP_FulfillmentContextModel,
    CarrierAccountCreateParams,
    Injections,
    StoreModel,
    Promise<PartnerCarrierAccountV2>
  >;
  updateCarrierAccount: Thunk<
    FP_FulfillmentContextModel,
    CarrierAccountUpdateParams,
    Injections,
    StoreModel,
    Promise<PartnerCarrierAccountV2>
  >;
  linkCarrierAccount: Thunk<
    FP_FulfillmentContextModel,
    LinkCarrierAccountParams,
    Injections,
    StoreModel,
    Promise<void>
  >;
  fetchCarrierAccounts: Thunk<
    FP_FulfillmentContextModel,
    void,
    Injections,
    StoreModel,
    Promise<PartnerCarrierAccountV2[]>
  >;
  fetchCarrierAccount: Thunk<
    FP_FulfillmentContextModel,
    string,
    Injections,
    StoreModel,
    Promise<PartnerCarrierAccountV2>
  >;
  carrierAccounts: Record<string, PartnerCarrierAccountV2>;
  setCarrierAccounts: Action<FP_FulfillmentContextModel, PartnerCarrierAccountV2[]>;
  setCarrierAccount: Action<FP_FulfillmentContextModel, PartnerCarrierAccountV2>;
  fetchCarrierAccountStart: Action<FP_FulfillmentContextModel, void>;
  fetchCarrierAccountLastAction?: number;

  fedexRegistrationsLastAction?: number;
  fedexRegistrations: Record<string, FedexRegistration>;
  setFedexRegistrations: Action<FP_FulfillmentContextModel, FedexRegistration[]>;
  setFedexRegistration: Action<FP_FulfillmentContextModel, FedexRegistration>;
  fetchFedexRegistrationsStart: Action<FP_FulfillmentContextModel, void>;
  fetchFedexRegistrations: Thunk<
    FP_FulfillmentContextModel,
    void,
    Injections,
    StoreModel,
    Promise<FedexRegistration[]>
  >;
  fetchFedexRegistration: Thunk<
    FP_FulfillmentContextModel,
    string,
    Injections,
    StoreModel,
    Promise<FedexRegistration[]>
  >;
  validateFedexAccountAddress: Thunk<FP_FulfillmentContextModel, any, Injections, StoreModel, Promise<any[]>>;
  validateFedexAccountInvoice: Thunk<FP_FulfillmentContextModel, any, Injections, StoreModel, Promise<any[]>>;
  generateFedexAccountPin: Thunk<FP_FulfillmentContextModel, any, Injections, StoreModel, Promise<any[]>>;
  validateFedexAccountPin: Thunk<FP_FulfillmentContextModel, any, Injections, StoreModel, Promise<any[]>>;
  resetFedexRegistration: Thunk<FP_FulfillmentContextModel, any, Injections, StoreModel, Promise<any[]>>;
  mutateFedexRegistrationFailure: Action<FP_FulfillmentContextModel, ApplicationErrorOptions>;

  upsRegistrationsLastAction?: number;
  upsRegistrations: Record<string, UPSRegistration>;
  carrierAccountUpsRegistrationMap: Computed<FP_FulfillmentContextModel, Record<string, UPSRegistration>>;
  setUpsRegistrations: Action<FP_FulfillmentContextModel, UPSRegistration[]>;
  setUpsRegistration: Action<FP_FulfillmentContextModel, UPSRegistration>;
  fetchUpsRegistrationsStart: Action<FP_FulfillmentContextModel, void>;
  fetchUpsRegistrations: Thunk<FP_FulfillmentContextModel, void, Injections, StoreModel, Promise<UPSRegistration[]>>;
  generateRegistrationLink: Thunk<
    FP_FulfillmentContextModel,
    { carrierAccountId: string; tenantId?: string },
    Injections,
    StoreModel,
    Promise<UPSRegistration>
  >;
  disconnectRegistrationLink: Thunk<
    FP_FulfillmentContextModel,
    { upsRegistrationId: string },
    Injections,
    StoreModel,
    Promise<UPSRegistration>
  >;
  fetchSharedAssetFailure: Action<FP_FulfillmentContextModel, ApplicationErrorOptions>;

  // injection

  //profile
  injectionProfiles: Record<string, InjectionProfile>;
  setInjectionProfiles: Action<FP_FulfillmentContextModel, InjectionProfile[]>;
  setInjectionProfile: Action<FP_FulfillmentContextModel, InjectionProfile>;
  fetchInjectionProfiles: Thunk<FP_FulfillmentContextModel, void, Injections, StoreModel, Promise<InjectionProfile[]>>;
  createInjectionProfile: Thunk<
    FP_FulfillmentContextModel,
    InjectionProfileCreateParams,
    Injections,
    StoreModel,
    Promise<InjectionProfile>
  >;
  updateInjectionProfile: Thunk<
    FP_FulfillmentContextModel,
    InjectionProfileUpdateParams,
    Injections,
    StoreModel,
    Promise<InjectionProfile>
  >;
  linkToInjectionProfile: Thunk<
    FP_FulfillmentContextModel,
    LinkToInjectionProfileParams,
    Injections,
    StoreModel,
    Promise<void>
  >;
  //site
  injectionSites: Record<string, InjectionSite>;
  setInjectionSites: Action<FP_FulfillmentContextModel, InjectionSite[]>;
  setInjectionSite: Action<FP_FulfillmentContextModel, InjectionSite>;
  fetchInjectionSites: Thunk<FP_FulfillmentContextModel, void, Injections, StoreModel, Promise<InjectionSite[]>>;
  createInjectionSite: Thunk<
    FP_FulfillmentContextModel,
    InjectionSiteCreateParams,
    Injections,
    StoreModel,
    Promise<InjectionSite>
  >;
  updateInjectionSite: Thunk<
    FP_FulfillmentContextModel,
    InjectionSiteUpdateParams,
    Injections,
    StoreModel,
    Promise<InjectionSite>
  >;
  linkToInjectionSite: Thunk<
    FP_FulfillmentContextModel,
    LinkToInjectionSiteParams,
    Injections,
    StoreModel,
    Promise<void>
  >;
  // Shipment Lane
  dockLanes: Record<string, PartnerLaneOrchestration>;
  dockLanePostalCodeMappings: Record<string, PartnerPostalCodeMappingCreate>;
  dockLaneSortConfigurations: Record<string, PartnerLaneSortConfigurationOrchestration>;
  dockLaneById: PartnerLaneOrchestration | {};
  dockLaneSchedules: Record<string, ScheduleEntry>;
  setDockLanes: Action<FP_FulfillmentContextModel, PartnerLaneOrchestration[]>;
  setDockLaneSortConfigurations: Action<FP_FulfillmentContextModel, PartnerLaneSortConfigurationOrchestration[]>;
  setDockLaneSchedules: Action<FP_FulfillmentContextModel, ScheduleEntry[]>;
  setDockLanePostalCodeMapping: Action<FP_FulfillmentContextModel, PartnerPostalCodeMappingCreate[]>;
  fetchDockLanes: Thunk<
    FP_FulfillmentContextModel,
    string | undefined,
    Injections,
    StoreModel,
    Promise<PartnerLaneOrchestration[]>
  >;

  fetchDockLane: Thunk<FP_FulfillmentContextModel, string, Injections, StoreModel, Promise<PartnerLaneOrchestration[]>>;

  fetchDockLaneSortConfigurations: Thunk<
    FP_FulfillmentContextModel,
    string | undefined,
    Injections,
    StoreModel,
    Promise<PartnerLaneSortConfigurationOrchestration>
  >;

  fetchDockLanePostalCodeMapping: Thunk<
    FP_FulfillmentContextModel,
    FetchDockLanePostalCodeMappingParams,
    Injections,
    StoreModel,
    Promise<DockLanePostalCodeResponse>
  >;

  // Third Party Billing
  // Sets
  thirdPartyBillingAccountSets: Record<string, Required<PartnerThirdPartyBillingAccountSet>>;
  hydratedThirdPartyBillingAccountSets: Record<string, HydratedThirdPartyBillingAccountSet>;
  fetchThirdPartyBillingAccountSets: Thunk<
    FP_FulfillmentContextModel,
    void,
    Injections,
    StoreModel,
    Promise<HydratedThirdPartyBillingAccountSet[]>
  >;
  setHydratedThirdPartyBillingAccountSets: Action<FP_FulfillmentContextModel, HydratedThirdPartyBillingAccountSet[]>;
  setThirdPartyBillingAccountSets: Action<FP_FulfillmentContextModel, Required<PartnerThirdPartyBillingAccountSet>[]>;
  setThirdPartyBillingAccountSet: Action<FP_FulfillmentContextModel, Required<PartnerThirdPartyBillingAccountSet>>;
  linkToThirdPartyBillingAccountSet: Thunk<
    FP_FulfillmentContextModel,
    LinkThirdPartyBillingAccountSetParams,
    Injections,
    StoreModel,
    Promise<void>
  >;
  // Accounts
  thirdPartyBillingAccounts: Record<string, ThirdPartyBillingAccount>;
  setThirdPartyBillingAccounts: Action<FP_FulfillmentContextModel, ThirdPartyBillingAccount[]>;
  setThirdPartyBillingAccount: Action<FP_FulfillmentContextModel, ThirdPartyBillingAccount>;
  fetchThirdPartyBillingAccounts: Thunk<
    FP_FulfillmentContextModel,
    void,
    Injections,
    StoreModel,
    Promise<ThirdPartyBillingAccount[]>
  >;
  createThirdPartyBillingAccount: Thunk<
    FP_FulfillmentContextModel,
    CreateThirdPartyBillingAccountParams,
    Injections,
    StoreModel,
    Promise<ThirdPartyBillingAccount>
  >;
  // updateThirdPartyBillingAccounts: Thunk<FP_FulfillmentContextModel, InjectionSiteUpdateParams, Injections, StoreModel, Promise<ThirdPartyBillingAccount>>;
  linkToThirdPartyBillingAccount: Thunk<
    FP_FulfillmentContextModel,
    LinkThirdPartyBillingAccountParams,
    Injections,
    StoreModel,
    Promise<void>
  >;

  fulfillmentContextTableSummaries: ContextTableSummary[];
  fulfillmentContextTableSummariesLoaded: boolean;
  setFulfillmentContextTableSummaries: Action<FP_FulfillmentContextModel, ContextTableSummary[]>;
}

export const FP_fulfillmentContextModel: FP_FulfillmentContextModel = {
  fulfillmentContextActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  /***
   * Fulfillment Context Summary
   */
  configuredCarrierServiceMethods: computed((state) => (contextId) => {
    const contexts = contextId
      ? { contextId: state.fulfillmentContextDetails[contextId] }
      : state.fulfillmentContextDetails;
    return carrierServiceMethodSummarizer(contexts);
  }),

  configuredCarriers: computed((state) => (contextId) => {
    const contexts = contextId
      ? { contextId: state.fulfillmentContextDetails[contextId] }
      : state.fulfillmentContextDetails;
    return carrierSummarizer(contexts);
  }),

  contextAuditInfoSummary: computed((state) => (contextId) => {
    const context = state.fulfillmentContextDetails[contextId];
    return context ? fulfillmentContextAudit(context) : undefined;
  }),

  /***
   * Fulfillment Context Details
   */
  fulfillmentContextDetails: {},
  fulfillmentContextTableSummaries: [],
  fulfillmentContextTableSummariesLoaded: false,
  setFulfillmentContextTableSummaries: action((state, payload) => {
    state.fulfillmentContextTableSummaries = payload;
    state.fulfillmentContextTableSummariesLoaded = true;
  }),

  /***
   * Lazy-loaded contracts and rate sets
   */
  fetchFulfillmentContextDetails: thunk(async (actions, payload, { injections, getState }) => {
    try {
      const { fulfillmentContextTableSummariesLoaded } = getState();
      const { fulfillmentContextService } = injections;
      if (!fulfillmentContextTableSummariesLoaded) {
        actions.fulfillmentContextActionStart();
        const [contexts, contacts, addresses, origins, schedules, tenants, tags] = await Promise.all([
          fulfillmentContextService.fetchFulfillmentContexts(),
          fulfillmentContextService.fetchContacts(),
          fulfillmentContextService.fetchAddresses(),
          fulfillmentContextService.fetchOrigins(),
          fulfillmentContextService.fetchSchedules(),
          fulfillmentContextService.fetchTenants(),
          fulfillmentContextService.fetchTags(),
        ]);
        const summary = buildContextTableSummaries({
          contexts,
          contacts,
          addresses,
          origins,
          schedules,
          tenants,
          tags,
        });
        actions.setFulfillmentContextTableSummaries(summary);
        return summary;
      }
    } catch (error) {
      actions.fetchFulfillmentContextDetailsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch fulfillment context details',
      });
      return error;
    }
  }),
  fetchFulfillmentContextDetailByContextId: thunk(async (actions, payload, { injections }) => {
    try {
      actions.fulfillmentContextActionStart();
      const { fulfillmentContextService } = injections;
      const [fctxDetail, tenants] = await Promise.all([
        fulfillmentContextService.fetchFulfillmentContextDetail(payload, undefined, true),
        fulfillmentContextService.fetchTenants(),
      ]);

      // workaround - call fetchFulfillmentContextDetail with tenant if we have a link
      // the tenants call is slower on average than fetchFulfillmentContextDetail, so by calling it
      // in parallel above, we save 100-200ms for fulfillment contexts that do NOT have linked tenants,
      // rather than calling tenants, searching for a link, then calling fetchFulfillmentContextDetail once in all cases.
      let fctxWithTenant;
      if (!fctxDetail.tenant) {
        const linkedTenant = tenants.find((tenant) =>
          tenant.links?.fulfillmentContexts?.find(
            (fctxLink) => fctxLink.shipiumId === fctxDetail?.fulfillmentContext?.fulfillmentContextId,
          ),
        );

        if (linkedTenant) {
          fctxWithTenant = await fulfillmentContextService.fetchFulfillmentContextDetail(
            payload,
            linkedTenant.tenantId,
            true,
          );
        }
      }

      const response = fctxWithTenant || fctxDetail;
      actions.setFulfillmentContextDetail(response);
      return response;
    } catch (error) {
      actions.fetchFulfillmentContextDetailFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch fulfillment context detail',
      });
      return error;
    }
  }),

  setFulfillmentContextDetails: action((state, contexts) => {
    const configMap = contexts.reduce<Record<string, FulfillmentContextDetail>>((acc, curr) => {
      acc[curr.fulfillmentContext.fulfillmentContextId] = curr;
      return acc;
    }, {});
    state.fulfillmentContextDetails = configMap;
  }),

  setFulfillmentContextDetail: action((state, context) => {
    state.fulfillmentContextDetails[context.fulfillmentContext.fulfillmentContextId] = context;
  }),

  fetchFulfillmentContextDetailsFailure: action((state, error) => {
    console.error(error);
  }),

  fetchFulfillmentContextDetailFailure: action((state, error) => {
    console.error(error);
  }),
  /***
   * Fulfillment Context Mutations
   */
  createFulfillmentContext: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contextId = await fulfillmentContextService.createFulfillmentContext(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'fulfillment-context-create',
        entityId: contextId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(contextId);
      return contextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  mutateFulfillmentContextFailure: action((state, error) => {
    console.error(error);
  }),
  deleteFulfillmentContext: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.deleteFulfillmentContext(payload);
      actions.setFulfillmentContextDetails([]);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'fulfillment-context-delete',
        entityId: payload,
      });

      await actions.fetchFulfillmentContextDetails();
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateFulfillmentContext: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contextId = await fulfillmentContextService.updateFulfillmentContext(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'fulfillment-context-update',
        entityId: payload.fulfillmentContextId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      return contextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  createOrigin: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const originId = await fulfillmentContextService.createOrigin(payload);

      await fulfillmentContextService.linkOrigin({
        fulfillmentContextId: payload.fulfillmentContextId,
        originId,
        linkAction: 'link',
      });
      if (payload.addressId) {
        await fulfillmentContextService.linkAddress({
          addressType: 'base',
          assetLinkType: 'origins',
          addressId: payload.addressId,
          linkAction: 'link',
          fulfillmentContextId: payload.fulfillmentContextId,
          assetId: originId,
        });
      }
      if (payload.contactId) {
        await fulfillmentContextService.linkContact({
          contactType: 'base',
          assetLinkType: 'origins',
          contactId: payload.contactId,
          linkAction: 'link',
          fulfillmentContextId: payload.fulfillmentContextId,
          assetId: originId,
        });
      }

      // Invalidate origins queries
      getStoreActions().queryInvalidation.invalidateQueries({ mutationType: 'origin-create', entityId: originId });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      return originId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      // Return null (not the error) so callers can tell the mutation failed and avoid navigating away
      // from the form — the failure is already surfaced to the user via the notification above.
      return null;
    }
  }),
  updateOrigin: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const originId = await fulfillmentContextService.updateOrigin(payload);

      if (payload.addressId) {
        await fulfillmentContextService.linkAddress({
          addressType: 'base',
          assetLinkType: 'origins',
          addressId: payload.addressId,
          linkAction: 'link',
          fulfillmentContextId: payload.fulfillmentContextId,
          assetId: originId,
        });
      }
      if (payload.contactId) {
        await fulfillmentContextService.linkContact({
          contactType: 'base',
          assetLinkType: 'origins',
          contactId: payload.contactId,
          linkAction: 'link',
          fulfillmentContextId: payload.fulfillmentContextId,
          assetId: originId,
        });
      }

      // Invalidate origins queries
      getStoreActions().queryInvalidation.invalidateQueries({ mutationType: 'origin-update', entityId: originId });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      return originId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      // Return null (not the error) so callers can tell the mutation failed and avoid navigating away
      // from the form — the failure is already surfaced to the user via the notification above.
      return null;
    }
  }),
  createReturnToAddressOverride: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const returnToAddressOverrideId = await fulfillmentContextService.createReturnToAddressOverride(payload);
      await fulfillmentContextService.linkReturnAddressOverride({
        returnToAddressOverrideId,
        fulfillmentContextId: payload.fulfillmentContextId,
      });

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'address-create',
        entityId: payload.fulfillmentContextId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      return returnToAddressOverrideId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateReturnToAddressOverride: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const updatedReturnToAddressOverride = await fulfillmentContextService.updateReturnAddressOverride(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'address-update',
        entityId: payload.returnToAddressOverrideId,
      });

      return updatedReturnToAddressOverride;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred. Please retry.',
      });
      return error;
    }
  }),
  unlinkReturnToAddressOverride: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contextId = await fulfillmentContextService.unlinkReturnToAddressOverrideFromFulfillmentContext(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'address-link',
        entityId: payload.fulfillmentContextId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId || '');
      return contextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred. Please retry.',
      });
      return error;
    }
  }),
  linkReturnToAddressOverride: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contextId = await fulfillmentContextService.linkReturnToAddressOverrideFromFulfillmentContext(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'address-link',
        entityId: payload.fulfillmentContextId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId || '');
      return contextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred. Please retry.',
      });
      return error;
    }
  }),
  linkAddressToReturnToAddressOverride: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contextId = await fulfillmentContextService.linkAddressToReturnAddressOverride(payload);
      // Invalidate address queries
      getStoreActions().queryInvalidation.invalidateQueries({ mutationType: 'address-link' });
      return contextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred. Please retry.',
      });
      return error;
    }
  }),
  unlinkAddressFromReturnToAddressOverride: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contextId = await fulfillmentContextService.unlinkAddressFromReturnAddressOverride(payload);
      // Invalidate address queries
      getStoreActions().queryInvalidation.invalidateQueries({ mutationType: 'address-delete' });

      return contextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred. Please retry.',
      });
      return error;
    }
  }),
  linkContactToReturnToAddressOverride: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contextId = await fulfillmentContextService.linkContactToReturnAddressOverride(payload);
      // Invalidate contact queries
      getStoreActions().queryInvalidation.invalidateQueries({ mutationType: 'contact-link' });
      return contextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred. Please retry.',
      });
      return error;
    }
  }),
  unlinkContactFromReturnToAddressOverride: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contextId = await fulfillmentContextService.unlinkContactFromReturnAddressOverride(payload);
      // Invalidate contact queries
      getStoreActions().queryInvalidation.invalidateQueries({ mutationType: 'contact-delete' });
      return contextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred. Please retry.',
      });
      return error;
    }
  }),
  createTenant: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const tenantId = await fulfillmentContextService.createTenant(payload);
      await fulfillmentContextService.linkTenant({
        tenantId,
        fulfillmentContextId: payload.fulfillmentContextId,
      });
      if (payload.usppiAddressId) {
        await fulfillmentContextService.linkAddress({
          addressType: 'usppiAddress',
          assetLinkType: 'tenants',
          addressId: payload.usppiAddressId,
          linkAction: 'link',
          fulfillmentContextId: payload.fulfillmentContextId,
          assetId: tenantId,
        });
      }
      if (payload.usppiContactId) {
        await fulfillmentContextService.linkContact({
          contactType: 'usppiContact',
          assetLinkType: 'tenants',
          contactId: payload.usppiContactId,
          linkAction: 'link',
          fulfillmentContextId: payload.fulfillmentContextId,
          assetId: tenantId,
        });
      }

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'tenant-create',
        entityId: payload.fulfillmentContextId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      return payload.fulfillmentContextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateTenant: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contextId = await fulfillmentContextService.updateTenant(payload);
      const { unitedStatesPrincipalPartyInInterestAddressId, unitedStatesPrincipalPartyInInterestContactId } =
        payload.currentContext?.tenant?.tenant || {};
      // unlink
      if (unitedStatesPrincipalPartyInInterestAddressId && !payload.usppiAddressId) {
        await fulfillmentContextService.linkAddress({
          addressType: 'usppiAddress',
          assetLinkType: 'tenants',
          addressId: unitedStatesPrincipalPartyInInterestAddressId,
          linkAction: 'unlink',
          fulfillmentContextId: payload.fulfillmentContextId,
          assetId: payload.tenantId,
        });
      }

      if (unitedStatesPrincipalPartyInInterestContactId && !payload.usppiContactId) {
        await fulfillmentContextService.linkContact({
          contactType: 'usppiContact',
          assetLinkType: 'tenants',
          contactId: unitedStatesPrincipalPartyInInterestContactId,
          linkAction: 'unlink',
          fulfillmentContextId: payload.fulfillmentContextId,
          assetId: payload.tenantId,
        });
      }

      if (payload.usppiAddressId && payload.tenantId) {
        if (payload.usppiAddressId) {
          await fulfillmentContextService.linkAddress({
            addressType: 'usppiAddress',
            assetLinkType: 'tenants',
            addressId: payload.usppiAddressId,
            linkAction: 'link',
            fulfillmentContextId: payload.fulfillmentContextId,
            assetId: payload.tenantId,
          });
        }
      }

      if (payload.usppiContactId && payload.tenantId) {
        await fulfillmentContextService.linkContact({
          contactType: 'usppiContact',
          assetLinkType: 'tenants',
          contactId: payload.usppiContactId,
          linkAction: 'link',
          fulfillmentContextId: payload.fulfillmentContextId,
          assetId: payload.tenantId,
        });
      }

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'tenant-update',
        entityId: payload.fulfillmentContextId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      return contextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  linkTenant: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contextId = await fulfillmentContextService.linkTenant(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'tenant-link',
        entityId: payload.fulfillmentContextId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      return contextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  unlinkTenant: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contextId = await fulfillmentContextService.unlinkTenant(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'tenant-link',
        entityId: payload.tenantId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(
        payload.unlinkTenantParams.fulfillmentContexts?.[0].id || '',
      );
      return contextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  linkOrigin: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contextId = await fulfillmentContextService.linkOrigin(payload);

      getStoreActions().queryInvalidation.invalidateQueries({ mutationType: 'origin-link' });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      return contextId;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),

  /***
   * Shared Assets
   */
  addresses: {},

  fetchAddressAssets: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      actions.fulfillmentContextActionStart();
      const response = await fulfillmentContextService.fetchAddresses();
      actions.setAddresses(response);
      return response;
    } catch (error) {
      actions.fetchFulfillmentContextDetailsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch fulfillment context details',
      });
      return error;
    }
  }),

  fetchSharedAssetFailure: action((state, error) => {
    console.error(error);
  }),

  setAddresses: action((state, addresses) => {
    state.addresses = addresses.reduce<Record<string, PartnerAddressV2>>((prev, curr) => {
      prev[curr.addressId!] = curr;
      return prev;
    }, {});
  }),
  setAddress: action((state, address) => {
    state.addresses[address.addressId!] = address;
  }),
  createAddress: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const address = await fulfillmentContextService.createAddress(payload);

      // Invalidate addresses queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'address-create',
        entityId: address.addressId,
      });

      actions.setAddress(address);
      return address;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateAddress: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const address = await fulfillmentContextService.updateAddress(payload);

      // Invalidate addresses queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'address-update',
        entityId: payload.addressId,
      });

      actions.setAddress(address);
      return address;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  linkAddress: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkAddress(payload);

      // Invalidate addresses queries
      getStoreActions().queryInvalidation.invalidateQueries({ mutationType: 'address-link' });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),

  contacts: {},

  fetchContactAssets: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      actions.fulfillmentContextActionStart();
      const response = await fulfillmentContextService.fetchContacts();
      actions.setContacts(response);
      return response;
    } catch (error) {
      actions.fetchFulfillmentContextDetailsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch fulfillment context details',
      });
      return error;
    }
  }),

  setContacts: action((state, contacts) => {
    state.contacts = contacts.reduce<Record<string, PartnerContact>>((prev, curr) => {
      prev[curr.contactId!] = curr;
      return prev;
    }, {});
  }),
  setContact: action((state, contact) => {
    state.contacts[contact.contactId!] = contact;
  }),
  createContact: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contact = await fulfillmentContextService.createContact(payload);

      // Invalidate contacts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'contact-create',
        entityId: contact.contactId,
      });

      actions.setContact(contact);
      return contact;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateContact: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const contact = await fulfillmentContextService.updateContact(payload);

      // Invalidate contacts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'contact-update',
        entityId: payload.contactId,
      });

      actions.setContact(contact);
      return contact;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  linkContact: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkContact(payload);

      // Invalidate contacts queries
      getStoreActions().queryInvalidation.invalidateQueries({ mutationType: 'contact-link' });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),

  linkTag: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkTag(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'tag-link',
        entityId: payload.fulfillmentContextId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  origins: {},
  hydratedOrigins: {},
  setOrigins: action((state, origins) => {
    for (const curr of origins) {
      state.origins[curr.originId] = curr;
    }
  }),
  setHydratedOrigins: action((state, payload) => {
    for (const curr of payload) {
      state.hydratedOrigins[curr.originId] = curr;
    }
  }),
  fetchOrigins: thunk(async (actions, payload, { injections }) => {
    try {
      actions.fulfillmentContextActionStart();
      const [addressList, contactList, scheduleList, tenantList, origins] = await Promise.all([
        injections.fulfillmentContextService.fetchAddresses(),
        injections.fulfillmentContextService.fetchContacts(),
        injections.fulfillmentContextService.fetchSchedules(),
        injections.fulfillmentContextService.fetchTenants(),
        injections.fulfillmentContextService.fetchOrigins(),
      ]);
      const hydratedOrigins = hydrateOrigins({
        addressList,
        contactList,
        scheduleList,
        tenantList,
        origins,
      });
      actions.setHydratedOrigins(hydratedOrigins);
      actions.setOrigins(origins);
    } catch (error) {
      actions.fetchSharedAssetFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),

  schedules: {},

  fetchScheduleAssets: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      actions.fulfillmentContextActionStart();
      const response = await fulfillmentContextService.fetchSchedules();
      actions.setSchedules(response);
      return response;
    } catch (error) {
      actions.fetchFulfillmentContextDetailsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch fulfillment context details',
      });
      return error;
    }
  }),

  setSchedules: action((state, contacts) => {
    state.schedules = contacts.reduce<Record<string, Schedule>>((prev, curr) => {
      prev[curr.scheduleId] = curr;
      return prev;
    }, {});
  }),
  setSchedule: action((state, contact) => {
    state.schedules[contact.scheduleId] = contact;
  }),
  createSchedule: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const schedule = await fulfillmentContextService.createSchedule(payload);
      if (schedule.scheduleId) {
        await fulfillmentContextService.linkSchedule({
          scheduleId: schedule.scheduleId,
          originId: payload.originId,
          linkAction: 'link',
          fulfillmentContextId: payload.fulfillmentContextId,
        });
      }

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'schedule-create',
        entityId: payload.fulfillmentContextId,
      });

      actions.setSchedule(schedule);
      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      return schedule;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateSchedule: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const schedule = await fulfillmentContextService.updateSchedule(payload);
      actions.setSchedule(schedule);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'schedule-update',
        entityId: payload.fulfillmentContextId,
      });
      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      return schedule;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  linkSchedule: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkSchedule(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'schedule-link',
        entityId: payload.fulfillmentContextId,
      });
      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),

  contracts: {},
  hydratedContracts: {},
  setContracts: action((state, payload) => {
    for (const val of payload) {
      state.contracts[val.contractId] = val;
    }
  }),
  setHydratedContracts: action((state, payload) => {
    for (const val of payload) {
      state.hydratedContracts[val.contractId] = val;
    }
  }),
  fetchContracts: thunk(async (actions, payload, { injections }) => {
    try {
      actions.fulfillmentContextActionStart();
      const [addressList, contactList, contracts, carrierSettingsList, carrierAccountList, tenants] = await Promise.all(
        [
          actions.fetchAddressAssets(),
          actions.fetchContactAssets(),
          injections.fulfillmentContextService.fetchContracts(),
          actions.fetchCarrierSettings(),
          actions.fetchCarrierAccounts(),
          injections.fulfillmentContextService.fetchTenants(),
        ],
      );
      const hydratedContracts = hydrateContracts({
        addressList,
        contactList,
        contracts,
        carrierSettingsList,
        carrierAccountList,
        tenants,
      });
      actions.setHydratedContracts(hydratedContracts);
      actions.setContracts(contracts);
      return contracts;
    } catch (error) {
      actions.fetchSharedAssetFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occured.  Please retry.',
      });
      return error;
    }
  }),
  labelProperties: {},

  fetchLabelProperties: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      actions.fulfillmentContextActionStart();
      const response = await fulfillmentContextService.fetchLabelProperties();
      actions.setLabelProperties(response);
      return response;
    } catch (error) {
      actions.fetchFulfillmentContextDetailsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch fulfillment label properties.',
      });
      return error;
    }
  }),

  setLabelProperties: action((state, labelProperties) => {
    state.labelProperties = labelProperties.reduce<Record<string, LabelProperties>>((prev, curr) => {
      prev[curr.labelPropertiesId] = curr;
      return prev;
    }, {});
  }),
  setLabelProperty: action((state, labelProperties) => {
    state.labelProperties[labelProperties.labelPropertiesId] = labelProperties;
  }),
  createLabelProperties: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const result = await fulfillmentContextService.createLabelProperties(payload);
      actions.setLabelProperty(result);
      return result;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateLabelProperties: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const result = await fulfillmentContextService.updateLabelProperties(payload);
      actions.setLabelProperty(result);
      return result;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  linkLabelProperty: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkLabelProperty(payload);
      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  carrierSettings: {},
  createCarrierSettings: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const settings = await fulfillmentContextService.createCarrierSettings(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'carrier-settings-create',
        entityId: settings.carrierSettingsId,
      });

      await actions.fetchCarrierSettings();
      return settings;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateCarrierSettings: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const settings = await fulfillmentContextService.updateCarrierSettings(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'carrier-settings-update',
        entityId: payload.carrierSettingsId,
      });

      await actions.fetchCarrierSettings();
      return settings;
    } catch (error) {
      actions.fetchFulfillmentContextDetailsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  fetchCarrierSettings: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const carrierSettings = await fulfillmentContextService.fetchCarrierSettings();
      actions.setCarrierSettings(carrierSettings);
      return carrierSettings;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  setCarrierSettings: action((state, payload) => {
    const map: Record<string, CarrierSettings> = {};
    for (const curr of payload) {
      map[curr.carrierSettingsId] = curr;
    }
    state.carrierSettings = map;
  }),
  linkCarrierSettings: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkCarrierSettings(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'carrier-settings-link',
        entityId: payload.fulfillmentContextId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  setContract: action((state, payload) => {
    state.contracts = { ...state.contracts, [payload.contractId]: payload };
  }),
  linkCarrierContract: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkCarrierContract(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'contract-link',
        entityId: payload.fulfillmentContextId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateCarrierContract: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.updateContract(payload);

      // Invalidate contracts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'contract-update',
        entityId: payload.contractId,
      });

      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  linkCarrierAccount: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkCarrierAccount(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'carrier-account-link',
        entityId: payload.fulfillmentContextId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  createCarrierAccount: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.createCarrierAccount(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'carrier-account-create',
        entityId: res.carrierAccountId,
      });

      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateCarrierAccount: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.updateCarrierAccount(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'carrier-account-update',
        entityId: payload.carrierAccountId,
      });

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  carrierAccounts: {},
  setCarrierAccounts: action((state, payload) => {
    const map: Record<string, PartnerCarrierAccountV2> = {};
    for (const val of payload) {
      map[val.carrierAccountId!] = val;
    }
    state.carrierAccounts = map;
  }),
  setCarrierAccount: action((state, payload) => {
    state.carrierAccounts[payload.carrierAccountId!] = payload;
  }),
  fetchCarrierAccounts: thunk(async (actions, payload, { injections }) => {
    try {
      actions.fetchCarrierAccountStart();
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.fetchCarrierAccounts();
      actions.setCarrierAccounts(res as unknown as PartnerCarrierAccountV2[]);
      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  fetchCarrierAccountStart: action((state) => {
    state.fetchCarrierAccountLastAction = Date.now();
  }),
  fetchCarrierAccount: thunk(async (actions, carrierAccountId, { injections }) => {
    try {
      actions.fetchCarrierAccountStart();
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.fetchCarrierAccount(carrierAccountId);
      actions.setCarrierAccount(res as unknown as PartnerCarrierAccountV2);

      if (res.shipperAddressId) {
        const shipperAddress = await fulfillmentContextService.fetchAddress(res.shipperAddressId);
        actions.setAddress(shipperAddress);
      }

      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  fedexRegistrations: {},
  setFedexRegistrations: action((state, payload) => {
    state.fedexRegistrations = {};
    for (const val of payload) {
      state.fedexRegistrations[val.carrierAccountId] = val;
    }
  }),
  setFedexRegistration: action((state, payload) => {
    state.fedexRegistrations = {
      ...state.fedexRegistrations,
      [payload.carrierAccountId]: payload,
    };
  }),
  fetchFedexRegistrationsStart: action((state) => {
    state.fedexRegistrationsLastAction = Date.now();
  }),
  fetchFedexRegistrations: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.fetchFedexRegistrationsStart();
      const state = getStoreState();
      const partnerId = state.session?.user?.sessionPartnerId || '';
      if (!partnerId) throw new Error('No valid session partnerId.');
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.fetchFedexRegistrations(partnerId);
      actions.setFedexRegistrations(res);
      return res;
    } catch (error) {
      actions.mutateFedexRegistrationFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  fetchFedexRegistration: thunk(async (actions, carrierAccountId, { injections, getStoreState }) => {
    try {
      actions.fetchFedexRegistrationsStart();
      const state = getStoreState();
      const partnerId = state.session?.user?.sessionPartnerId || '';
      if (!partnerId) throw new Error('No valid session partnerId.');
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.fetchFedexRegistration(partnerId, carrierAccountId);
      actions.setFedexRegistration(res);
      return res;
    } catch (error) {
      actions.mutateFedexRegistrationFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  validateFedexAccountAddress: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const {
        carrierAccountId,
        address,
        usePastAddress = false,
        mockFedexChildKey = '',
        mockFedexChildSecret = '',
      } = payload;
      actions.fetchFedexRegistrationsStart();
      const state = getStoreState();
      const partnerId = state.session?.user?.sessionPartnerId || '';
      if (!partnerId) throw new Error('No valid session partnerId.');
      const res = await injections.fulfillmentContextService.validateFedexAccountAddress(partnerId, carrierAccountId, {
        ...(usePastAddress ? usePastAddress : {}),
        ...(mockFedexChildKey ? mockFedexChildKey : {}),
        ...(mockFedexChildSecret ? mockFedexChildSecret : {}),
        ...address,
      });
      actions.setFedexRegistration(res);
      return res;
    } catch (error) {
      actions.mutateFedexRegistrationFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  validateFedexAccountInvoice: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const { carrierAccountId, invoice } = payload;
      actions.fetchFedexRegistrationsStart();
      const state = getStoreState();
      const partnerId = state.session?.user?.sessionPartnerId || '';
      if (!partnerId) throw new Error('No valid session partnerId.');
      const res = await injections.fulfillmentContextService.validateFedexAccountInvoice(
        partnerId,
        carrierAccountId,
        invoice,
      );
      actions.setFedexRegistration(res);
      return res;
    } catch (error) {
      actions.mutateFedexRegistrationFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  generateFedexAccountPin: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const { carrierAccountId, pinBody } = payload;
      actions.fetchFedexRegistrationsStart();
      const state = getStoreState();
      const partnerId = state.session?.user?.sessionPartnerId || '';
      if (!partnerId) throw new Error('No valid session partnerId.');
      const res = await injections.fulfillmentContextService.generateFedexAccountPin(
        partnerId,
        carrierAccountId,
        pinBody,
      );
      actions.setFedexRegistration(res);
      return res;
    } catch (error) {
      actions.mutateFedexRegistrationFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  validateFedexAccountPin: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const { carrierAccountId, pinBody } = payload;
      actions.fetchFedexRegistrationsStart();
      const state = getStoreState();
      const partnerId = state.session?.user?.sessionPartnerId || '';
      if (!partnerId) throw new Error('No valid session partnerId.');
      const res = await injections.fulfillmentContextService.validateFedexAccountPin(
        partnerId,
        carrierAccountId,
        pinBody,
      );
      actions.setFedexRegistration(res);
      return res;
    } catch (error) {
      actions.mutateFedexRegistrationFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  resetFedexRegistration: thunk(async (actions, carrierAccountId, { injections, getStoreState }) => {
    try {
      actions.fetchFedexRegistrationsStart();
      const state = getStoreState();
      const partnerId = state.session?.user?.sessionPartnerId || '';
      if (!partnerId) throw new Error('No valid session partnerId.');
      const res = await injections.fulfillmentContextService.resetFedexRegistration(partnerId, carrierAccountId);
      actions.setFedexRegistration(res);
      return res;
    } catch (error) {
      actions.mutateFedexRegistrationFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  mutateFedexRegistrationFailure: action((state, error) => {
    console.error(error);
  }),

  upsRegistrations: {},
  carrierAccountUpsRegistrationMap: computed(({ upsRegistrations }) => {
    return Object.values(upsRegistrations ?? {}).reduce((prev: Record<string, any>, val) => {
      if (
        !prev[val.carrierAccountId] ||
        new Date(prev[val.carrierAccountId].lastUpdatedTimestamp) < new Date(val.lastUpdatedTimestamp)
      ) {
        prev[val.carrierAccountId] = val;
      }
      return prev;
    }, {});
  }),
  setUpsRegistrations: action((state, payload) => {
    state.upsRegistrations = {};
    for (const val of payload) {
      state.upsRegistrations[val.upsRegistrationId] = val;
    }
  }),
  setUpsRegistration: action((state, payload) => {
    state.upsRegistrations = {
      ...state.upsRegistrations,
      [payload.upsRegistrationId]: payload,
    };
  }),
  fetchUpsRegistrationsStart: action((state) => {
    state.upsRegistrationsLastAction = Date.now();
  }),
  fetchUpsRegistrations: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.fetchUpsRegistrationsStart();
      const state = getStoreState();
      const partnerId = state.session?.user?.sessionPartnerId || '';
      if (!partnerId) throw new Error('No valid session partnerId.');
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.fetchUpsRegistrations(partnerId);
      actions.setUpsRegistrations(res);
      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  generateRegistrationLink: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const state = getStoreState();
      const { carrierAccountId, tenantId } = payload;
      const partnerId = state.session?.user?.sessionPartnerId || '';
      if (!partnerId) throw new Error('No valid session partnerId.');
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.generateUPSRegistration({
        carrierAccountId,
        tenantId,
        partnerId,
      });
      actions.setUpsRegistration(res);
      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  disconnectRegistrationLink: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const state = getStoreState();
      const { upsRegistrationId } = payload;
      if (!upsRegistrationId) throw new Error('Registration link required.');
      const partnerId = state.session?.user?.sessionPartnerId || '';
      if (!partnerId) throw new Error('No valid session partnerId.');
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.disconnectUPSRegistration(upsRegistrationId);
      actions.fetchCarrierAccounts();
      actions.setUpsRegistration(res);
      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  injectionProfiles: {},
  setInjectionProfiles: action((state, payload) => {
    state.injectionProfiles = {};
    for (const val of payload) {
      state.injectionProfiles[val.injectionProfileId] = val;
    }
  }),
  setInjectionProfile: action((state, payload) => {
    state.injectionProfiles = {
      ...state.injectionProfiles,
      [payload.injectionProfileId]: payload,
    };
  }),
  fetchInjectionProfiles: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.fetchInjectionProfiles();
      actions.setInjectionProfiles(res);
      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  createInjectionProfile: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.createInjectionProfile(payload);
      actions.setInjectionProfile(res);
      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateInjectionProfile: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.updateInjectionProfile(payload);
      actions.setInjectionProfile(res);
      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  linkToInjectionProfile: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkToInjectionProfile(payload);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),

  labelAugmentations: {},

  fetchLabelAugmentations: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      actions.fulfillmentContextActionStart();
      const response = await fulfillmentContextService.fetchLabelAugmentations();
      actions.setLabelAugmentations(response);
      return response;
    } catch (error) {
      actions.fetchFulfillmentContextDetailsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occured, unable to fetch fulfillment label properties.',
      });
      return error;
    }
  }),

  setLabelAugmentations: action((state, labelAugmentations) => {
    state.labelAugmentations = labelAugmentations.reduce<Record<string, Augmentation>>((prev, curr) => {
      prev[curr.labelAugmentationId] = curr;
      return prev;
    }, {});
  }),
  setLabelAugmentation: action((state, labelAugmentation) => {
    state.labelAugmentations[labelAugmentation.labelAugmentationId] = labelAugmentation;
  }),
  createLabelAugmentation: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const result = await fulfillmentContextService.createLabelAugmentation(payload);
      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      await actions.fetchLabelAugmentations();
      return result;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  injectionSites: {},
  setInjectionSite: action((state, payload) => {
    state.injectionSites = {
      ...state.injectionSites,
      [payload.injectionSiteId]: payload,
    };
  }),
  setInjectionSites: action((state, payload) => {
    state.injectionSites = {};
    for (const val of payload) {
      state.injectionSites[val.injectionSiteId] = val;
    }
  }),
  fetchInjectionSites: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.fetchInjectionSites();
      actions.setInjectionSites(res);
      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateLabelAugmentation: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const result = await fulfillmentContextService.updateLabelAugmentation(payload);
      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
      await actions.fetchLabelAugmentations();
      return result;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  createInjectionSite: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const existingProfile = payload.fulfillmentContextDetail.injectionProfiles.find(
        (ip) => ip.injectionProfile.carrierId === payload.carrierId,
      );
      let injectionProfileToLink: InjectionProfile;
      // if there is an injection profile but it is not shipium generated
      // it will need to be unlinked
      let unlinkInjectionProfileFromContext = false;
      // if we are creating a new injection profile
      // we will need to link it to the fulfillment context
      let linkInjectionToProfile = false;
      // if there is an existing injection profile but
      // it is of a different transportation type
      // we need to update the profile

      // we can't actually edit the injectionProfile type via api
      // so I am disabling this for now, but there is open question to product on this
      // let updateExistingProfile =false;

      // using the convention of partner provided id === fulfillmentcontextid + carrierId
      // to determine if existing injection profile is shipium generated
      const expectedKey = `${payload.fulfillmentContextDetail.fulfillmentContext.fulfillmentContextId}_${payload.carrierId}`;
      // the create payload will be the same no matter what
      // so we define it for reuse
      const createPayload = {
        carrierId: payload.carrierId,
        name: 'SHIPIUM_GENERATED',
        partnerProvidedId: expectedKey,
        description: 'Injection Profile automatically generated by the shipium UI',
        type: payload.type as any,
      };
      const createdInjectionSite = await fulfillmentContextService.createInjectionSite({
        name: payload.name,
        partnerProvidedId: payload.partnerProvidedId,
        description: payload.description,
        carrierId: payload.carrierId,
        supportedDestinations: payload.supportedDestinations,
        timeInTransitDays: payload.timeInTransitDays,
        rateModifier: payload.rateModifier,
        limitDestinations: payload.limitDestinations,
      });
      let injectionSitesToLink = [createdInjectionSite.injectionSiteId];
      if (existingProfile && existingProfile.injectionProfile) {
        if (existingProfile.injectionProfile.partnerProvidedId !== expectedKey) {
          injectionProfileToLink = await fulfillmentContextService.createInjectionProfile(createPayload);
          injectionSitesToLink = [...injectionSitesToLink, ...existingProfile.injectionProfile.injectionSiteIds];
          unlinkInjectionProfileFromContext = true;
          linkInjectionToProfile = true;
        } else {
          injectionProfileToLink = existingProfile.injectionProfile;
          // if(existingProfile.injectionProfile.type !== payload.type){
          //   updateExistingProfile = true;
          // }
        }
      } else {
        injectionProfileToLink = await fulfillmentContextService.createInjectionProfile(createPayload);
        linkInjectionToProfile = true;
      }
      if (unlinkInjectionProfileFromContext && existingProfile) {
        await fulfillmentContextService.linkToInjectionProfile({
          injectionProfileId: existingProfile.injectionProfile.injectionProfileId,
          linkedAssetId: payload.fulfillmentContextDetail.fulfillmentContext.fulfillmentContextId,
          linkAction: 'unlink',
          linkType: 'fulfillmentContext',
        });
      }
      // see above comment
      // if(updateExistingProfile && existingProfile) {
      //   await fulfillmentContextService.updateInjectionProfile({
      //     ...existingProfile.injectionProfile,
      //     type:payload.type as any
      //   });
      // }
      if (linkInjectionToProfile) {
        await fulfillmentContextService.linkToInjectionProfile({
          injectionProfileId: injectionProfileToLink.injectionProfileId,
          linkedAssetId: payload.fulfillmentContextDetail.fulfillmentContext.fulfillmentContextId,
          linkAction: 'link',
          linkType: 'fulfillmentContext',
        });
      }
      injectionSitesToLink.forEach(async (siteId: string) => {
        await fulfillmentContextService.linkToInjectionProfile({
          injectionProfileId: injectionProfileToLink.injectionProfileId,
          linkedAssetId: siteId,
          linkAction: 'link',
          linkType: 'injectionSites',
        });
      });
      await fulfillmentContextService.linkToInjectionSite({
        linkAction: 'link',
        linkType: 'contact',
        linkedAssetId: payload.contact,
        injectionSiteId: createdInjectionSite.injectionSiteId,
      });
      await fulfillmentContextService.linkToInjectionSite({
        linkAction: 'link',
        linkType: 'address',
        linkedAssetId: payload.address,
        injectionSiteId: createdInjectionSite.injectionSiteId,
      });
      actions.fetchFulfillmentContextDetailByContextId(
        payload.fulfillmentContextDetail.fulfillmentContext.fulfillmentContextId,
      );
      actions.setInjectionSite(createdInjectionSite);
      return createdInjectionSite;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  linkLabelAugmentation: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkLabelAugmentation(payload);
      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  updateInjectionSite: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.updateInjectionSite({
        injectionSiteId: payload.injectionSiteId,
        carrier: payload.carrierId,
        name: payload.name,
        description: payload.description,
        limitDestinations: payload.limitDestinations,
        supportedDestinations: payload.supportedDestinations,
        timeInTransitDays: payload.timeInTransitDays,
        rateModifier: payload.rateModifier,
        partnerProvidedId: payload.partnerProvidedId,
        active: true,
        archived: false,
      });
      actions.setInjectionSite(res);
      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),

  linkToInjectionSite: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkToInjectionSite(payload);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  dockLanes: {},
  dockLaneById: {},
  dockLaneSortConfigurations: {},
  dockLaneSchedules: {},
  dockLanePostalCodeMappings: {},
  setDockLanes: action((state, payload) => {
    state.dockLanes = {};
    for (const val of payload) {
      state.dockLanes[val.laneId as string] = val;
    }
  }),
  setDockLaneSortConfigurations: action((state, payload) => {
    state.dockLaneSortConfigurations = {};
    for (const val of payload) {
      state.dockLaneSortConfigurations[val.laneSortConfigurationId as string] = val;
    }
  }),
  setDockLaneSchedules: action((state, payload) => {
    state.dockLaneSchedules = {};
    for (const val of payload) {
      state.dockLaneSchedules[val.scheduleEntryId as string] = val;
    }
  }),
  setDockLanePostalCodeMapping: action((state, payload) => {
    state.dockLanePostalCodeMappings = {};
    for (const val of payload) {
      state.dockLanePostalCodeMappings[val.laneId as string] = val;
    }
  }),
  fetchDockLanes: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const dockLanes = await fulfillmentContextService.fetchDockLanes(payload);
      actions.setDockLanes(dockLanes);
      return dockLanes;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred. Please retry.',
      });
      return error;
    }
  }),
  fetchDockLane: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const dockLane = await fulfillmentContextService.fetchDockLane(payload);
      actions.setDockLanes([dockLane]);
      return dockLane;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred. Please retry.',
      });
      return error;
    }
  }),
  fetchDockLaneSortConfigurations: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const dockLaneSortConfigurations = await fulfillmentContextService.fetchDockLaneSortConfiguration(payload);
      if (dockLaneSortConfigurations.results) {
        actions.setDockLaneSortConfigurations(dockLaneSortConfigurations.results);
        return dockLaneSortConfigurations.results;
      } else {
        actions.setDockLaneSortConfigurations([dockLaneSortConfigurations]);
        return dockLaneSortConfigurations;
      }
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred. Please retry.',
      });
      return error;
    }
  }),
  fetchDockLanePostalCodeMapping: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const dockLanePostalCodeMappings = await fulfillmentContextService.fetchDockLanePostalMapping(payload);
      return dockLanePostalCodeMappings;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred. Please retry.',
      });
      return error;
    }
  }),
  thirdPartyBillingAccountSets: {},
  setThirdPartyBillingAccountSets: action((state, payload) => {
    state.thirdPartyBillingAccountSets = {};
    for (const val of payload) {
      state.thirdPartyBillingAccountSets[val.thirdPartyBillingAccountSetId] = val;
    }
  }),
  hydratedThirdPartyBillingAccountSets: {},
  setHydratedThirdPartyBillingAccountSets: action((state, payload) => {
    state.hydratedThirdPartyBillingAccountSets = {};
    for (const val of payload) {
      state.hydratedThirdPartyBillingAccountSets[val.thirdPartyBillingAccountSetId] = val;
    }
  }),
  fetchThirdPartyBillingAccountSets: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const [thirdPartyBillingAccountSetList, thirdPartyBillingAccountList, tenantList] = await Promise.all([
        fulfillmentContextService.fetchThirdPartyBillingAccountSets(),
        fulfillmentContextService.fetchThirdPartyBillingAccounts(),
        fulfillmentContextService.fetchTenants(),
      ]);
      const thirdPartyBillingAccounts: Record<string, (typeof thirdPartyBillingAccountList)[number]> = {};
      for (const curr of thirdPartyBillingAccountList) {
        thirdPartyBillingAccounts[curr.thirdPartyBillingAccountId] = curr;
      }
      const hydratedThirdPartyBillingAccountSet = thirdPartyBillingAccountSetList.map((thirdPartyBillingAccountSet) =>
        hydrateThirdPartyBillingAccountSet({
          thirdPartyBillingAccountSet,
          thirdPartyBillingAccounts,
          tenantList,
        }),
      );
      actions.setHydratedThirdPartyBillingAccountSets(hydratedThirdPartyBillingAccountSet);
      actions.setThirdPartyBillingAccountSets(thirdPartyBillingAccountSetList);
      return hydratedThirdPartyBillingAccountSet;
    } catch (error) {
      actions.fetchFulfillmentContextDetailsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  setThirdPartyBillingAccountSet: action((state, payload) => {
    state.thirdPartyBillingAccountSets = {
      ...state.thirdPartyBillingAccountSets,
      [payload.thirdPartyBillingAccountSetId]: payload,
    };
  }),
  linkToThirdPartyBillingAccountSet: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkThirdPartyBillingAccountSet(payload);

      await actions.fetchFulfillmentContextDetailByContextId(payload.fulfillmentContextId);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  // Accounts
  thirdPartyBillingAccounts: {},
  setThirdPartyBillingAccounts: action((state, payload) => {
    state.thirdPartyBillingAccounts = {};
    for (const val of payload) {
      state.thirdPartyBillingAccounts[val.thirdPartyBillingAccountId] = val;
    }
  }),
  setThirdPartyBillingAccount: action((state, payload) => {
    state.thirdPartyBillingAccounts = {
      ...state.thirdPartyBillingAccounts,
      [payload.thirdPartyBillingAccountId]: payload,
    };
  }),
  fetchThirdPartyBillingAccounts: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.fetchThirdPartyBillingAccounts();
      actions.setThirdPartyBillingAccounts(res);
      return res;
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  createThirdPartyBillingAccount: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      return await fulfillmentContextService.createThirdPartyBillingAccount(payload);
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
  linkToThirdPartyBillingAccount: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentContextService } = injections;
      await fulfillmentContextService.linkThirdPartyBillingAccount(payload);

      await actions.fetchThirdPartyBillingAccountSets();
    } catch (error) {
      actions.mutateFulfillmentContextFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'An error occurred.  Please retry.',
      });
      return error;
    }
  }),
};
